import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { RecordContextProvider, useGetOne, usePermissions, useTranslate } from 'react-admin';
import { Box } from '@mui/material';
import { RightDrawer } from '@/components/global/drawers/RightDrawer.jsx';
import { DetailsSampleList } from '@/components/global/fields/DetailsSampleList.jsx';
import { ChangeHistory } from '@/components/global/history/ChangeHistory.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';

export const DetailsShow = ({
    handleClose,
    elementId,
    resource,
    canView,
    actions,
    children,
    footer,
    title,
    showChangeHistory = false,
    historyFieldsSource,
    formatHistoryFieldLabel,
    formatHistoryFieldChanges,
    formatHistoryChangeValue,
}) => {
    const translate = useTranslate();
    const { closeAllDrawers } = useDrawer();
    const { permissions, isLoading: isPermissionsLoading } = usePermissions();
    const allowed = Boolean(canView?.(permissions));
    const { data, isPending, error } = useGetOne(resource, { id: elementId }, { enabled: Boolean(elementId) && allowed && !isPermissionsLoading });

    useEffect(() => {
        if (!isPermissionsLoading && !allowed) {
            closeAllDrawers();
        }
    }, [allowed, closeAllDrawers, isPermissionsLoading]);

    if (isPermissionsLoading || !allowed) {
        return null;
    }

    const loadingState = getLoadingState({ isLoading: isPending, error, data, translate });
    if (loadingState) {
        return loadingState;
    }

    return (
        <RecordContextProvider value={data}>
            <RightDrawer
                handleClose={handleClose}
                title={title || translate('common.details')}
                actions={actions}
                after={
                    showChangeHistory ? (
                        <ChangeHistory
                            resource={resource}
                            recordId={elementId}
                            fieldsSource={historyFieldsSource}
                            formatFieldLabel={formatHistoryFieldLabel}
                            formatFieldChanges={formatHistoryFieldChanges}
                            formatChangeValue={formatHistoryChangeValue}
                        />
                    ) : null
                }
            >
                <Box className='show-page'>
                    <DetailsSampleList record={data}>{children}</DetailsSampleList>
                    {footer}
                </Box>
            </RightDrawer>
        </RecordContextProvider>
    );
};

DetailsShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resource: PropTypes.string.isRequired,
    canView: PropTypes.func.isRequired,
    actions: PropTypes.node,
    children: PropTypes.node,
    footer: PropTypes.node,
    title: PropTypes.string,
    showChangeHistory: PropTypes.bool,
    historyFieldsSource: PropTypes.string,
    formatHistoryFieldLabel: PropTypes.func,
    formatHistoryFieldChanges: PropTypes.func,
    formatHistoryChangeValue: PropTypes.func,
};
